import { motion } from 'framer-motion'
import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import {
    getDailyArtwork,
    getFallbackArtwork,
    getTodayCharacter,
} from '../../services/pixivService'

const DailyContainer = styled(motion.div)`
    width: 100%;
    max-width: 520px;
    margin: 0 auto 50px;

    @media (max-width: 768px) {
        max-width: 400px;
        margin: 0 auto 40px;
    }

    @media (max-width: 480px) {
        max-width: 340px;
        margin: 0 auto 32px;
    }
`

const Header = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 20px;
    padding: 0 4px;

    @media (max-width: 768px) {
        margin-bottom: 16px;
    }
`

const Title = styled.h2`
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
    
    @media (max-width: 768px) {
        font-size: 0.7rem;
        letter-spacing: 2.5px;
    }
`

const DateLabel = styled.span`
    font-size: 0.7rem;
    font-weight: 400;
    letter-spacing: 1px;
    color: rgba(255, 255, 255, 0.5);
    font-variant-numeric: tabular-nums;
    
    @media (max-width: 768px) {
        font-size: 0.65rem;
    }
`

const ImageFrame = styled(motion.div)`
    position: relative;
    width: 100%;
    aspect-ratio: 3 / 4;
    overflow: hidden;
    background: rgba(0, 0, 0, 0.2);
    box-shadow: 
        0 8px 32px rgba(0, 0, 0, 0.2),
        0 0 0 1px rgba(255, 255, 255, 0.1);
    
    /* 角落装饰线 - 左上 */
    &::before {
        content: '';
        position: absolute;
        top: 12px;
        left: 12px;
        width: 24px;
        height: 24px;
        border-top: 1px solid rgba(255, 255, 255, 0.25);
        border-left: 1px solid rgba(255, 255, 255, 0.25);
        pointer-events: none;
        z-index: 2;
    }
    
    /* 角落装饰线 - 右下 */
    &::after {
        content: '';
        position: absolute;
        bottom: 12px;
        right: 12px;
        width: 24px;
        height: 24px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.25);
        border-right: 1px solid rgba(255, 255, 255, 0.25);
        pointer-events: none;
        z-index: 2;
    }

    @media (max-width: 480px) {
        &::before, &::after {
            width: 16px;
            height: 16px;
        }
    }
`

const ArtworkImage = styled(motion.img)`
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top;
    filter: saturate(0.95);
    transition: filter 0.6s ease;
    
    ${ImageFrame}:hover & {
        filter: saturate(1.05);
    }
`

const LoadingPlaceholder = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.3);
`

const LoadingText = styled.span`
    font-size: 0.65rem;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.4);
`

const Footer = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-top: 18px;
    padding: 0 4px;
    gap: 20px;

    @media (max-width: 768px) {
        margin-top: 14px;
    }
`

const ArtworkInfo = styled.div`
    flex: 1;
    min-width: 0;
    text-align: left;
`

const CharacterName = styled.h3`
    font-size: 0.9rem;
    font-weight: 500;
    letter-spacing: 1.5px;
    color: rgba(255, 255, 255, 0.85);
    margin: 0 0 4px 0;
    text-align: left;
    
    @media (max-width: 768px) {
        font-size: 0.8rem;
    }
`

const ArtistCredit = styled.a`
    display: inline-block;
    font-size: 0.7rem;
    font-weight: 400;
    letter-spacing: 0.5px;
    color: rgba(255, 255, 255, 0.45);
    text-decoration: none;
    transition: color 0.3s ease;
    text-align: left;
    
    &:hover {
        color: rgba(255, 255, 255, 0.75);
    }
    
    @media (max-width: 768px) {
        font-size: 0.65rem;
    }
`

const SourceLink = styled.a`
    font-size: 0.65rem;
    font-weight: 500;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.4);
    text-decoration: none;
    transition: color 0.3s ease;
    flex-shrink: 0;
    align-self: center;
    
    &:hover {
        color: rgba(255, 255, 255, 0.7);
    }
    
    @media (max-width: 768px) {
        font-size: 0.55rem;
    }
`

// 格式化日期
const formatDate = () => {
    const today = new Date()
    return today.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    }).toUpperCase()
}

const DailyTypeMoon = () => {
    const [imageLoaded, setImageLoaded] = useState(false)
    const [artworkData, setArtworkData] = useState(null)
    
    // 初始化加载今日作品
    useEffect(() => {
        const character = getTodayCharacter()
        const artwork = getDailyArtwork(character)
        setArtworkData(artwork || getFallbackArtwork(character))
    }, [])
    
    // 图片加载错误处理 - 切换到本地 fallback
    const handleImageError = useCallback(() => {
        if (artworkData && !artworkData.isFallback) {
            console.warn('Pixiv image failed to load, using fallback')
            setArtworkData(getFallbackArtwork(artworkData.character))
            setImageLoaded(false)
        }
    }, [artworkData])
    
    return (
        <DailyContainer
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
        >
            <Header>
                <Title>Daily Type-Moon</Title>
                <DateLabel>{formatDate()}</DateLabel>
            </Header>
            
            <ImageFrame
                whileHover={{ scale: 1.005 }}
                transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            >
                {artworkData ? (
                    <ArtworkImage
                        src={artworkData.imageUrl}
                        alt={`Daily ${artworkData.characterDisplay} artwork`}
                        initial={{ opacity: 0, scale: 1.05 }}
                        animate={{ 
                            opacity: imageLoaded ? 1 : 0,
                            scale: imageLoaded ? 1 : 1.05
                        }}
                        transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
                        onLoad={() => setImageLoaded(true)}
                        onError={handleImageError}
                    />
                ) : null}
                
                {!imageLoaded && (
                    <LoadingPlaceholder>
                        <LoadingText>Loading...</LoadingText>
                    </LoadingPlaceholder>
                )}
            </ImageFrame>
            
            {artworkData && (
                <Footer>
                    <ArtworkInfo>
                        <CharacterName>{artworkData.characterDisplay}</CharacterName>
                        <ArtistCredit 
                            href={artworkData.pixivUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            by {artworkData.artistName}
                        </ArtistCredit>
                    </ArtworkInfo>
                    <SourceLink
                        href={artworkData.pixivUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Pixiv →
                    </SourceLink>
                </Footer>
            )}
        </DailyContainer>
    )
}

export default DailyTypeMoon
